import { defaultRangeExtractor, type Range, useVirtualizer } from '@tanstack/react-virtual';
import { memo, useCallback, useMemo, useRef } from 'react';

import { cn } from '@air/utils-tailwind';

import { SelectedItem } from '~/components/v2/components/shared/selectionBar/components/SelectedItem';
import { SelectedItemGroupHeader } from '~/components/v2/components/shared/selectionBar/components/SelectedItemGroupHeader';
import { useSelectedItemsList } from '~/components/v2/components/shared/selectionBar/hooks/useSelectedItemsList';

export type SelectionListProps = {
  scrollElement: HTMLElement | null;
  allowUnselect?: boolean;
};
export const SelectionList = memo(({ scrollElement, allowUnselect = true }: SelectionListProps) => {
  const activeStickyIndexRef = useRef<number | null>(null);

  const { itemList } = useSelectedItemsList();

  const estimateSize = useCallback(
    (index: number) => {
      const item = itemList[index];
      const baseSize = item.type === 'header' ? 24 : 64;
      const gap = 4;

      return baseSize + gap;
    },
    [itemList],
  );

  const stickyIndices = useMemo(
    () =>
      itemList
        .map((item, index) => (item.type === 'header' ? index : null))
        .filter((idx): idx is number => idx !== null),
    [itemList],
  );

  const isSticky = useCallback((index: number) => stickyIndices.includes(index), [stickyIndices]);

  const isActiveSticky = useCallback((index: number) => activeStickyIndexRef.current === index, []);

  const virtualizer = useVirtualizer({
    count: itemList.length,
    getScrollElement: () => scrollElement ?? null,
    estimateSize,
    overscan: 5,
    scrollMargin: scrollElement?.offsetTop ?? 0,
    rangeExtractor: useCallback(
      (range: Range) => {
        activeStickyIndexRef.current = [...stickyIndices].reverse().find((index) => range.startIndex >= index) ?? null;

        const next = new Set([
          ...(activeStickyIndexRef.current !== null ? [activeStickyIndexRef.current] : []),
          ...defaultRangeExtractor(range),
        ]);

        return [...next].sort((a, b) => a - b);
      },
      [stickyIndices],
    ),
  });

  const virtualItems = virtualizer.getVirtualItems();

  return (
    <div className="relative w-full" style={{ height: virtualizer.getTotalSize() }}>
      {virtualItems.map((virtualRow, index) => {
        const listItem = itemList[virtualRow.index];
        const isItemSticky = isSticky(virtualRow.index);
        const isItemActiveSticky = isActiveSticky(virtualRow.index);
        const isLastVirtualItem = index === virtualItems.length - 1;
        const isHeader = listItem.type === 'header';

        return (
          <div
            key={virtualRow.key}
            data-index={virtualRow.index}
            ref={virtualizer.measureElement}
            className={cn(
              'px-3',
              isItemSticky ? 'z-20' : 'z-10',
              isHeader && isItemActiveSticky && 'bg-grey-1',
              isHeader && !isItemActiveSticky && 'pt-3',
              (isHeader && isItemActiveSticky) || isLastVirtualItem ? 'pb-3' : 'pb-1',
            )}
            style={{
              position: isItemActiveSticky ? 'sticky' : 'absolute',
              top: isItemActiveSticky ? 48 : 0,
              left: 0,
              width: '100%',
              ...(!isItemActiveSticky && {
                transform: `translateY(${virtualRow.start}px)`,
              }),
            }}
          >
            {listItem.type === 'header' ? (
              <SelectedItemGroupHeader listItem={listItem} />
            ) : (
              <SelectedItem item={listItem.item} allowUnselect={allowUnselect} />
            )}
          </div>
        );
      })}
    </div>
  );
});

SelectionList.displayName = 'SelectionList';
